/**
 * SafetyModalsManager Component
 *
 * Central coordinator for all safety modals. Renders the appropriate modal
 * based on SafetyModalsContext state.
 *
 * Responsibilities:
 * - Renders ToS, CrisisResources, CrisisPractitionerPrompt, and ConcernFlag modals
 * - Single point of integration in app layout
 * - Enforces modal priority via context state
 *
 * Usage:
 * Place as a sibling to main app content inside SafetyModalsProvider:
 *
 * ```tsx
 * <SafetyModalsProvider>
 *   <AppContent />
 *   <SafetyModalsManager />
 * </SafetyModalsProvider>
 * ```
 *
 * Created: 2025-12-18
 * Phase: Moderation & Safety Infrastructure (Week 2) + Phase 2.5 (Crisis Practitioner)
 */

import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useSafetyModals } from '../../contexts/SafetyModalsContext';
// Import ModalType directly from source - re-export resolves to undefined on web
import { ModalType } from '../../contexts/safety-modals/types-constants';
import { ErrorBoundary } from '../ErrorBoundary';
import { TosModal } from './TosModal';
import { ConcernFlagModal } from './ConcernFlagModal';
import { CrisisResourcesModal } from './CrisisResourcesModal';
import { CrisisPractitionerPrompt } from './CrisisPractitionerPrompt';
import { CrisisDetectionFeedbackModal } from './CrisisDetectionFeedbackModal';
import type { FalsePositiveCategory } from '../../lib/analytics/events/safety';
import { captureEvent } from '../../lib/analytics';

/**
 * SafetyModalsFallback - Minimal fallback UI when safety modals crash
 *
 * P1 SECURITY: Ensures user always has access to crisis resources
 * even if modal rendering fails (memory pressure, JS errors, etc.)
 */
function SafetyModalsFallback() {
  return (
    <View style={fallbackStyles.container}>
      <View style={fallbackStyles.content}>
        <Text style={fallbackStyles.title}>Need Help?</Text>
        <Text style={fallbackStyles.subtitle}>
          If you&apos;re experiencing a crisis, please contact:
        </Text>
        <View style={fallbackStyles.resourceRow}>
          <Text style={fallbackStyles.resourceLabel}>988 Suicide & Crisis Lifeline</Text>
          <Text style={fallbackStyles.resourceNumber}>Call or text 988</Text>
        </View>
        <View style={fallbackStyles.resourceRow}>
          <Text style={fallbackStyles.resourceLabel}>Crisis Text Line</Text>
          <Text style={fallbackStyles.resourceNumber}>Text HOME to 741741</Text>
        </View>
      </View>
    </View>
  );
}

const fallbackStyles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#FEF2F2',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#FECACA',
  },
  content: {
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: '#991B1B',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#7F1D1D',
    marginBottom: 12,
    textAlign: 'center',
  },
  resourceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: 300,
    marginBottom: 8,
  },
  resourceLabel: {
    fontSize: 14,
    color: '#374151',
    fontWeight: '500',
  },
  resourceNumber: {
    fontSize: 14,
    color: '#2563EB',
    fontWeight: '600',
  },
});

export interface SafetyModalsManagerProps {
  /**
   * Optional callback when ToS is accepted
   */
  onTosAccepted?: () => void;

  /**
   * Optional callback when concern is submitted
   */
  onConcernSubmitted?: () => void;

  /**
   * Optional callback when crisis assistance request is sent
   */
  onPractitionerRequestSent?: (practitionerId: string) => void;

  /**
   * Optional callback when detection feedback is submitted (Phase 3)
   */
  onDetectionFeedbackSubmitted?: (wasAccurate: boolean, category?: FalsePositiveCategory) => void;
}

/**
 * SafetyModalsManager - Coordinator for all safety modals
 *
 * Renders modals based on context state:
 * - ToS Modal: When ToS needs acceptance
 * - Crisis Resources Modal: When crisis detected or user-initiated
 * - Crisis Practitioner Prompt: When user can reach out to practitioner
 * - Concern Flag Modal: When user reports concern
 *
 * Priority handling is managed by SafetyModalsContext.
 */
export function SafetyModalsManager({
  onTosAccepted,
  onConcernSubmitted,
  onPractitionerRequestSent,
  onDetectionFeedbackSubmitted,
}: SafetyModalsManagerProps) {
  const { state, closeModal } = useSafetyModals();

  const handleResourceClose = () => {
    closeModal(ModalType.CRISIS_RESOURCES);
  };

  const handlePractitionerClose = () => {
    closeModal(ModalType.CRISIS_PRACTITIONER);
  };

  // Get crisis practitioner modal data
  const practitionerData = state.modals[ModalType.CRISIS_PRACTITIONER]?.data as
    | { moderationEventId?: string; severity?: 'low' | 'medium' | 'high' | 'critical' }
    | undefined;

  return (
    <ErrorBoundary
      fallback={<SafetyModalsFallback />}
      componentName="SafetyModalsManager"
      onError={(error) => {
        captureEvent('Safety_ModalsCrashed', {
          error_message: error.message,
          error_name: error.name,
        });
      }}
    >
      {/* ToS Modal - Highest priority, blocks everything */}
      <TosModal onAccepted={onTosAccepted} />

      {/* Crisis Resources Modal - High priority */}
      <CrisisResourcesModal
        visible={state.modals[ModalType.CRISIS_RESOURCES].isVisible}
        severity={state.crisisSeverity || 'medium'}
        crisisTriggered={state.crisisDetected}
        onClose={handleResourceClose}
      />

      {/* Crisis Practitioner Prompt - Medium-High priority */}
      <CrisisPractitionerPrompt
        visible={state.modals[ModalType.CRISIS_PRACTITIONER].isVisible}
        moderationEventId={practitionerData?.moderationEventId}
        severity={practitionerData?.severity || state.crisisSeverity || 'medium'}
        onClose={handlePractitionerClose}
        onRequestSent={onPractitionerRequestSent}
      />

      {/* Concern Flag Modal - Medium priority */}
      <ConcernFlagModal onSubmitted={onConcernSubmitted} />

      {/* Detection Feedback Modal - Low priority (Phase 3) */}
      <CrisisDetectionFeedbackModal onSubmitted={onDetectionFeedbackSubmitted} />
    </ErrorBoundary>
  );
}

export default SafetyModalsManager;
